import { useEffect, useRef, useState } from 'react';

interface InfoRowProps {
  icon: string;
  label: string;
  children: React.ReactNode;
}

function InfoRow({ icon, label, children }: InfoRowProps) {
  return (
    <div className="flex gap-4 py-4 border-b border-gray-100 last:border-0">
      <div className="w-8 h-8 flex items-center justify-center text-green-600 shrink-0 mt-0.5">
        <i className={`${icon} text-lg`} />
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</p>
        <div className="text-gray-700 text-sm leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

export default function StoreInfoSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="access" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-14">
          <span className="text-green-600 text-sm font-semibold tracking-widest uppercase">Access</span>
          <h2 className="text-3xl font-bold text-gray-800 mt-2">店舗情報・アクセス</h2>
          <div className="w-12 h-1 bg-green-600 mx-auto mt-4 rounded-full" />
        </div>

        <div
          ref={ref}
          className="grid grid-cols-1 lg:grid-cols-2 gap-10"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          {/* Info Table */}
          <div className="bg-white rounded-lg border border-gray-100 p-8">
            <h3 className="font-bold text-gray-800 text-xl mb-6">カーサービス田中自動車</h3>

            <InfoRow icon="ri-map-pin-2-line" label="住所">
              〒000-0000 ○○県○○市○○町 0-0-0
            </InfoRow>

            <InfoRow icon="ri-phone-line" label="電話番号">
              <a href="tel:0000000000" className="text-green-600 font-semibold hover:underline">
                000-000-0000
              </a>
            </InfoRow>

            <InfoRow icon="ri-time-line" label="営業時間">
              月曜日〜土曜日　8:00〜18:00
            </InfoRow>

            <InfoRow icon="ri-calendar-close-line" label="定休日">
              日曜日・祝日
            </InfoRow>

            <InfoRow icon="ri-road-map-line" label="アクセス">
              <p>○○インターチェンジより車で約5分</p>
              <p className="mt-1">○○幹線道路沿い、○○交差点を北へ300m</p>
              <p className="mt-1">駐車場：あり（無料）</p>
            </InfoRow>

            <InfoRow icon="ri-line-fill" label="LINE公式アカウント">
              <a
                href="https://lin.ee/xxxxxxx"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 mt-1 font-semibold px-4 py-2 rounded-md text-white text-sm hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: '#06C755' }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-line-fill" />
                </div>
                LINEで問い合わせる
              </a>
            </InfoRow>
          </div>

          {/* Google Map */}
          <div className="rounded-lg overflow-hidden border border-gray-100 min-h-[400px]">
            <iframe
              title="カーサービス田中自動車 地図"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.7476680378087!2d139.69167!3d35.6894875!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188ca575e5bbd5%3A0x38a5cc1e00f23fdb!2z5paw5a6_!5e0!3m2!1sja!2sjp!4v1699999999999"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px', display: 'block' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
